import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * Parity pass 3: the upstream note modal. Two things it does that a plain
 * textarea does not — a formatting toolbar that wraps the selection, and a
 * live preview, with the honest "Saved as text" chip (the webapp stores
 * markdown-ish plain text, not rich content).
 */

const CHAR_LIMIT = 4000;

/** The upstream RichNoteText renderer, reduced to its actual vocabulary. */
export function renderNote(text: string) {
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const numbered = /^\s*(\d+)\.\s+(.*)$/.exec(line);
    const body = bullet ? bullet[1] : numbered ? numbered[2] : line;
    const html = body
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/(^|[^*])\*([^*]+?)\*/g, "$1<em>$2</em>")
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-coral-dark underline">$1</a>');
    if (!line.trim()) return <div key={i} className="h-2" />;
    return (
      <div key={i} className={cn("leading-6", (bullet || numbered) && "flex gap-2")}>
        {bullet ? <span className="text-muted-foreground">•</span> : null}
        {numbered ? <span className="text-muted-foreground">{numbered[1]}.</span> : null}
        <span dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    );
  });
}

export function TaskNoteDialog({
  open,
  onOpenChange,
  title,
  value,
  saving,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  value: string;
  saving: boolean;
  onSave: (next: string) => void;
}) {
  const [text, setText] = useState(value);
  const [ref, setRef] = useState<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (open) setText(value);
  }, [open, value]);

  /** Wrap or prefix the current selection, as the upstream toolbar does. */
  const apply = (kind: "bold" | "italic" | "link" | "bullet" | "number") => {
    if (!ref) return;
    const start = ref.selectionStart;
    const end = ref.selectionEnd;
    const sel = text.slice(start, end);
    let next: string;
    let caret: number;
    if (kind === "bold" || kind === "italic") {
      const mark = kind === "bold" ? "**" : "*";
      next = `${text.slice(0, start)}${mark}${sel || kind}${mark}${text.slice(end)}`;
      caret = start + mark.length + (sel || kind).length + mark.length;
    } else if (kind === "link") {
      next = `${text.slice(0, start)}[${sel || "text"}](https://)${text.slice(end)}`;
      caret = next.length;
    } else {
      const prefix = kind === "bullet" ? "- " : "1. ";
      const lineStart = text.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
      next = `${text.slice(0, lineStart)}${prefix}${text.slice(lineStart)}`;
      caret = start + prefix.length;
    }
    setText(next);
    requestAnimationFrame(() => {
      ref.focus();
      ref.setSelectionRange(caret, caret);
    });
  };

  const btn = "h-7 px-2 text-xs";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-1 rounded-md border border-border bg-muted/30 p-1">
          <Button variant="ghost" size="sm" className={cn(btn, "font-bold")} onClick={() => apply("bold")}>B</Button>
          <Button variant="ghost" size="sm" className={cn(btn, "italic")} onClick={() => apply("italic")}>I</Button>
          <Button variant="ghost" size="sm" className={btn} onClick={() => apply("link")}>Link</Button>
          <Button variant="ghost" size="sm" className={btn} onClick={() => apply("bullet")}>• List</Button>
          <Button variant="ghost" size="sm" className={btn} onClick={() => apply("number")}>1. List</Button>
          <span className="ml-auto rounded-full bg-background px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground ring-1 ring-border">
            Saved as text
          </span>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <Textarea
            ref={setRef}
            rows={12}
            value={text}
            maxLength={CHAR_LIMIT}
            onChange={(e) => setText(e.target.value)}
            className="font-mono text-sm"
            placeholder="Write the note…"
          />
          <div className="max-h-[19rem] overflow-y-auto rounded-md border border-border bg-muted/20 p-3 text-sm">
            {text.trim() ? (
              renderNote(text)
            ) : (
              <span className="text-muted-foreground">Nothing to preview yet.</span>
            )}
          </div>
        </div>

        <DialogFooter className="items-center">
          <span className="mr-auto text-xs tabular-nums text-muted-foreground">
            {text.length} / {CHAR_LIMIT}
          </span>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" disabled={saving} onClick={() => onSave(text)}>
            {saving ? "Saving…" : "Save note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TaskNoteDialog;
